import { QueryClient } from '@tanstack/query-core';
import axios, { type AxiosInstance, type AxiosRequestConfig, type Method } from 'axios';
import axiosRetry from 'axios-retry';

import { getConfig } from '../config';
import { type AppError, InternalServerError, UnauthorizedError } from '../error';
import { convertError } from '../error/convert';
import { trackError } from '../error/tracking';
import { captureException } from '../logger/capture-exception';
import { useStore as useUserStore } from '../services/storage/users';

const UNAUTH_RETRY_MAX_COUNT = 5;
const RETRY_TIME_MS = 500;
let queryClient: undefined | QueryClient;

export function getQueryClient() {
  if (queryClient != null) {
    return queryClient;
  }

  const newClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes - data is fresh for 5 min
        gcTime: 1000 * 60 * 30, // 30 minutes - cache garbage collection
        refetchOnWindowFocus: false, // Don't refetch on tab focus
        retry: 1, // Retry failed queries once
      },
    },
  });
  queryClient = newClient;

  return newClient;
}

function getAxiosApiInstance(isAuthRequired: boolean, authToken: string): AxiosInstance {
  const axiosInstance = axios.create({
    withCredentials: true,
  });

  // setup the token when possible
  axiosInstance.interceptors.request.use(function (config) {
    if (isAuthRequired && authToken.length === 0) {
      throw new UnauthorizedError('token is required');
    }

    if (config == null) {
      return config;
    }

    config.headers = config.headers ?? ({} as any as typeof config.headers);
    config.headers.Authorization = authToken.length > 0 ? `Bearer ${authToken}` : '';

    return config;
  });

  // handle errors
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const err = convertError(error);
      if (err instanceof UnauthorizedError) {
        // eslint-disable-next-line no-console
        console.error('unauthorized request', err);
        // TODO: need to log out from clerk as well for example
        // setAuthToken('');
        // unsetSocket();
      }

      return Promise.reject(err);
    }
  );

  // TODO: why? v1 is erroring because of this
  // retry on network errors and 5XX responses
  // TODO: solve this typing issue
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  axiosRetry(axiosInstance as any, { retries: 2 });

  return axiosInstance;
}

export function buildQuery(data?: { [key: string]: any } | null): string {
  const params = new URLSearchParams(data ?? {});
  return params.toString();
}

async function forceSignOut(err?: Error | AppError, extraInfo?: { [key: string]: any }) {
  await useUserStore.getState().signOut(); // force the sign out

  // wait a second to make sure all went through
  setTimeout(function () {
    window.location.href = '/auth';
  }, 500);

  const errParsed = convertError(err ?? new InternalServerError('forced sign out'));
  captureException(
    errParsed,
    {
      file: 'sdk/request.ts',
      method: 'forceSignOut',
      infoMessage: `force sign out requested`,
      ...(extraInfo ?? {}),
    },
    trackError
  );
}

export async function apiRequest<T>(
  endpoint: string,
  method: Method,
  isAuthRequired: boolean,
  data?: { [key: string]: any } | object | null,
  config?: AxiosRequestConfig,
  reqAuthToken?: string,
  dontCatchUnauthorized?: boolean,
  unauthRetryCount?: number
): Promise<T> {
  const token = reqAuthToken ?? (await useUserStore.getState().getAuthToken());
  let parsedEndpoint = endpoint;

  // NOTE: E2E is a very specific case where we want to inform the backend
  //       that we just want mocks
  if (getConfig().isE2eTest) {
    parsedEndpoint =
      parsedEndpoint.indexOf('?') !== -1
        ? `${parsedEndpoint}&mock=true`
        : `${parsedEndpoint}?mock=true`;
  }

  try {
    const axiosInstance = getAxiosApiInstance(isAuthRequired, token);
    const res = await axiosInstance.request<T>({
      url: parsedEndpoint,
      method,
      withCredentials: true,
      baseURL: getConfig().api,
      data,
      ...(config ?? {}),
    });

    return res.data;
  } catch (err) {
    if (!(err instanceof UnauthorizedError)) {
      throw err;
    }

    // maybe we can still be smart about it and check the session on window
    if (dontCatchUnauthorized) {
      await forceSignOut(err, {
        requestUrl: endpoint,
        requestMethod: method,
        requestData: data,
      });
      throw err;
    }

    // maybe we do have a token just not updated
    const newToken = await useUserStore.getState().getAuthToken(true);

    // being the same token says that it will become unauthorized again
    if (newToken !== token && newToken.length > 0) {
      return apiRequest<T>(endpoint, method, isAuthRequired, data, config, newToken, true);
    }

    // keep retrying until we get to the max count
    const parsedCount = (unauthRetryCount ?? 0) + 1;
    if (parsedCount < UNAUTH_RETRY_MAX_COUNT) {
      await new Promise(function (resolve) {
        setTimeout(function () {
          resolve(undefined);
        }, RETRY_TIME_MS);
      });

      // make the request again
      return apiRequest<T>(
        endpoint,
        method,
        isAuthRequired,
        data,
        config,
        newToken,
        false,
        parsedCount
      );
    }

    // already went through the counts so, lets force now
    await forceSignOut(err, {
      requestUrl: endpoint,
      requestMethod: method,
      requestData: data,
      sameToken: newToken === token,
      doesntHaveToken: newToken.length === 0,
    });
    throw err;
  }
}

export function setURL(url: string, query?: { [key: string]: unknown } | null): string {
  if (query == null) {
    return url;
  }

  const queryRaw = [] as string[];
  for (const key of Object.keys(query)) {
    const value = query[key];
    if (value == null) {
      continue;
    }

    // TODO: solve this typing issue
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    queryRaw.push(`${key}=${String(value)}`);
  }

  if (queryRaw.length === 0) {
    return url;
  }

  return `${url}?${queryRaw.join('&')}`;
}
